<template>
  <div class="vivo">
    <!-- C1: its own page, so the list screen is no longer carrying a form that
         is always open. One component serves /departments/new and
         /departments/:id/edit — `isEdit` is driven by the route param, the same
         way the admin portal's formPages() routes work. -->
    <div class="row between" style="margin-bottom: 14px">
      <div>
        <div class="pagetitle">
          {{ isEdit ? $t("departmentForm.editTitle") : $t("departmentForm.addTitle") }}
        </div>
        <div class="crumb">
          {{ $t("common.company") }} / {{ $t("departments.title") }} /
          {{ isEdit ? $t("departmentForm.crumbEdit") : $t("departmentForm.crumbNew") }}
        </div>
      </div>
      <button class="btn ghost" type="button" data-test="dept-form-cancel" @click="goList">
        {{ $t("departmentForm.backToList") }}
      </button>
    </div>

    <div v-if="loading" class="state">{{ $t("departmentForm.loading") }}</div>
    <div v-else-if="loadError" class="state" data-test="dept-form-load-error">
      {{ $t("departmentForm.loadError") }}
    </div>

    <div v-else class="panel">
      <div class="panel__head">
        <h4>{{ isEdit ? $t("departmentForm.editTitle") : $t("departmentForm.addTitle") }}</h4>
        <span v-if="!isEdit" class="tag ok">{{ $t("departments.defaultActiveTag") }}</span>
      </div>
      <div class="panel__body">
        <div v-if="saveError" class="note" data-test="dept-form-error">
          {{ $t("departmentForm.errors." + saveError) }}
        </div>

        <form class="form three" novalidate @submit.prevent="submit">
          <div class="field">
            <label>{{ $t("departments.fieldName") }} <span class="req">*</span></label>
            <input
              class="input"
              :class="{ err: !!fieldErrors.nameAr }"
              data-test="dept-name"
              :placeholder="$t('departments.fieldNamePlaceholder')"
              v-model="form.nameAr"
            />
            <small v-if="fieldErrors.nameAr" class="xs" style="color: var(--v-danger)">
              {{ $t("departmentForm.fieldErrors." + fieldErrors.nameAr) }}
            </small>
          </div>
          <div class="field">
            <label>{{ $t("departments.fieldManager") }}</label>
            <input
              class="input"
              :class="{ err: !!fieldErrors.managerName }"
              data-test="dept-manager"
              :placeholder="$t('departments.fieldManagerPlaceholder')"
              v-model="form.managerName"
            />
            <small v-if="fieldErrors.managerName" class="xs" style="color: var(--v-danger)">
              {{ $t("departmentForm.fieldErrors." + fieldErrors.managerName) }}
            </small>
          </div>
          <div class="field">
            <label>{{ $t("departments.fieldStatus") }}</label>
            <select class="select" data-test="dept-status" v-model="form.isActive">
              <option :value="true">{{ $t("departments.statusActive") }}</option>
              <option :value="false">{{ $t("departments.statusSuspended") }}</option>
            </select>
          </div>
        </form>

        <div class="divider"></div>
        <div class="row" style="gap: 10px">
          <button
            class="btn primary"
            type="button"
            data-test="dept-save"
            :disabled="saving"
            @click="submit"
          >
            {{ saving ? $t("departmentForm.saving") : $t("departments.save") }}
          </button>
          <button class="btn ghost" type="button" :disabled="saving" @click="goList">
            {{ $t("departments.cancel") }}
          </button>
        </div>
        <div class="note info mt">
          <b>{{ $t("departments.noteLabel") }}</b>{{ $t("departments.note") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiGet, apiPost, apiPatch } from "../../api/vivo/http";
import {
  blankDepartment,
  departmentToForm,
  validateDepartment,
  buildDepartmentPayload,
  saveErrorKey
} from "./department-form-vm";

export default {
  name: "VivoDepartmentForm",
  data() {
    return {
      form: blankDepartment(),
      fieldErrors: {},
      saveError: "",
      saving: false,
      loading: false,
      loadError: false
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    }
  },
  async mounted() {
    if (!this.isEdit) return;
    // Edit mode re-reads the row instead of trusting state handed over from the
    // list: a deep link to /departments/7/edit has no list behind it, and a
    // stale row would silently overwrite someone else's newer change.
    this.loading = true;
    try {
      const row = await apiGet("/departments/" + this.$route.params.id);
      this.form = departmentToForm(row);
    } catch (e) {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    goList() {
      if (this.$route.name !== "departments") this.$router.push({ name: "departments" });
    },
    async submit() {
      if (this.saving) return;
      this.fieldErrors = validateDepartment(this.form);
      if (Object.keys(this.fieldErrors).length) {
        this.saveError = "validation";
        return;
      }
      this.saveError = "";
      this.saving = true;
      const body = buildDepartmentPayload(this.form);
      try {
        if (this.isEdit) {
          await apiPatch("/departments/" + this.$route.params.id, body);
        } else {
          await apiPost("/departments", body);
        }
        // "ولما احفظ البيانات تفتح الصفحة اللي فيها كل الاقسام" — land back on
        // the full list, and tell it what just happened so it can say so.
        this.$router.push({
          name: "departments",
          query: { saved: this.isEdit ? "edited" : "created", name: body.nameAr }
        });
      } catch (e) {
        this.saveError = saveErrorKey(e);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
